import React, {
	createContext,
	useContext,
	useState,
	useRef,
	useEffect,
	useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { cn } from '@quantum-ecosystem/ui-toolkit';

const MenuContext = createContext(null);

export const FormMenu = ({
	children,
	opened,
	defaultOpened = false,
	onChange,
	onClose,
	trigger = 'click',
	closeOnClickOutside = true,
	closeOnItemClick = true,
	closeOnEscape = true,
	position,
	align: propAlign,
	width,
	className,
	style,
	...restProps
}) => {
	const [internalOpen, setInternalOpen] = useState(defaultOpened);
	const isControlled = opened !== undefined;
	const isOpen = isControlled ? Boolean(opened) : internalOpen;
	const wrapperRef = useRef(null);
	const timeoutRef = useRef(null);

	const align =
		propAlign ||
		(position && (position.includes('end') || position.includes('right'))
			? 'right'
			: 'left');

	const setOpen = useCallback(
		(nextOpen) => {
			if (!isControlled) {
				setInternalOpen(nextOpen);
			}
			onChange?.(nextOpen);
			if (!nextOpen) {
				onClose?.();
			}
		},
		[isControlled, onChange, onClose]
	);

	const toggle = useCallback(() => {
		setOpen(!isOpen);
	}, [isOpen, setOpen]);

	const close = useCallback(() => {
		setOpen(false);
	}, [setOpen]);

	const handleMouseEnter = () => {
		if (trigger === 'hover') {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			setOpen(true);
		}
	};

	const handleMouseLeave = () => {
		if (trigger === 'hover') {
			timeoutRef.current = setTimeout(() => {
				setOpen(false);
			}, 150);
		}
	};

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!isOpen || !closeOnClickOutside) return;

		const handleClickOutside = (event) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
				setOpen(false);
			}
		};

		const handleKeyDown = (event) => {
			if (closeOnEscape && event.key === 'Escape') {
				setOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, closeOnClickOutside, closeOnEscape, setOpen]);

	return (
		<MenuContext.Provider
			value={{
				isOpen,
				setOpen,
				toggle,
				close,
				trigger,
				closeOnItemClick,
				align,
				openUpwards: position?.startsWith('top'),
				width,
			}}
		>
			<div
				ref={wrapperRef}
				className={cn('q-dropdown-menu-wrapper', className)}
				style={style}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				{...restProps}
			>
				{children}
			</div>
		</MenuContext.Provider>
	);
};

const MenuTarget = ({ children, className, ...restProps }) => {
	const ctx = useContext(MenuContext);
	if (!ctx) {
		throw new Error('Menu.Target must be used inside FormMenu');
	}

	const handleClick = (e) => {
		if (ctx.trigger === 'click') {
			ctx.toggle();
		}
	};

	if (React.isValidElement(children)) {
		return React.cloneElement(children, {
			onClick: (e) => {
				children.props?.onClick?.(e);
				handleClick(e);
			},
			'aria-haspopup': 'menu',
			'aria-expanded': ctx.isOpen,
			className: cn(children.props.className, className),
			...restProps,
		});
	}

	return (
		<span
			role="button"
			tabIndex={0}
			onClick={handleClick}
			aria-haspopup="menu"
			aria-expanded={ctx.isOpen}
			className={className}
			{...restProps}
		>
			{children}
		</span>
	);
};

const MenuDropdown = ({ children, className, style, ...restProps }) => {
	const ctx = useContext(MenuContext);
	if (!ctx) {
		throw new Error('Menu.Dropdown must be used inside FormMenu');
	}

	if (!ctx.isOpen) return null;

	const widthStyle = ctx.width
		? {
				width: typeof ctx.width === 'number' ? `${ctx.width}px` : ctx.width,
				minWidth:
					typeof ctx.width === 'number' ? `${ctx.width}px` : ctx.width,
		  }
		: {};

	return (
		<div
			role="menu"
			className={cn(
				'q-dropdown-menu-content',
				ctx.align === 'right'
					? 'q-dropdown-menu-align-right'
					: 'q-dropdown-menu-align-left',
				ctx.openUpwards && 'q-dropdown-menu-open-top',
				className
			)}
			style={{
				...widthStyle,
				...style,
			}}
			{...restProps}
		>
			{children}
		</div>
	);
};

const MenuItem = ({
	children,
	component: Component = 'button',
	leftSection,
	icon,
	rightSection,
	variant = 'default',
	color,
	disabled = false,
	onClick,
	className,
	style,
	...restProps
}) => {
	const ctx = useContext(MenuContext);
	const iconElement = leftSection || icon;

	const handleClick = (e) => {
		if (disabled) return;
		onClick?.(e);
		if (ctx?.closeOnItemClick) {
			ctx?.close();
		}
	};

	const isDanger = variant === 'danger' || color === 'red';

	return (
		<Component
			type={Component === 'button' ? 'button' : undefined}
			role="menuitem"
			disabled={disabled}
			onClick={handleClick}
			className={cn(
				'q-dropdown-menu-item',
				isDanger && 'q-dropdown-menu-item-danger',
				className
			)}
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: rightSection ? 'space-between' : 'flex-start',
				textDecoration: 'none',
				...style,
			}}
			{...restProps}
		>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: '10px',
					minWidth: 0,
					flex: 1,
				}}
			>
				{iconElement && (
					<span className="q-dropdown-menu-item-icon">{iconElement}</span>
				)}
				<span
					style={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{children}
				</span>
			</div>
			{rightSection && (
				<span
					style={{
						marginLeft: 'auto',
						display: 'inline-flex',
						alignItems: 'center',
						flexShrink: 0,
					}}
				>
					{rightSection}
				</span>
			)}
		</Component>
	);
};

const MenuDivider = ({ className, style, ...restProps }) => {
	return (
		<div
			role="separator"
			className={cn('q-dropdown-menu-divider', className)}
			style={style}
			{...restProps}
		/>
	);
};

const MenuLabel = ({ children, className, style, ...restProps }) => {
	return (
		<div
			className={cn('q-dropdown-menu-header', className)}
			style={style}
			{...restProps}
		>
			{children}
		</div>
	);
};

FormMenu.Target = MenuTarget;
FormMenu.Dropdown = MenuDropdown;
FormMenu.Item = MenuItem;
FormMenu.Divider = MenuDivider;
FormMenu.Label = MenuLabel;

FormMenu.propTypes = {
	children: PropTypes.node,
	opened: PropTypes.bool,
	defaultOpened: PropTypes.bool,
	onChange: PropTypes.func,
	onClose: PropTypes.func,
	trigger: PropTypes.oneOf(['click', 'hover']),
	closeOnClickOutside: PropTypes.bool,
	closeOnItemClick: PropTypes.bool,
	closeOnEscape: PropTypes.bool,
	position: PropTypes.string,
	align: PropTypes.oneOf(['left', 'right']),
	width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	className: PropTypes.string,
	style: PropTypes.object,
};

MenuTarget.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};

MenuDropdown.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	style: PropTypes.object,
};

MenuItem.propTypes = {
	children: PropTypes.node,
	component: PropTypes.elementType,
	leftSection: PropTypes.node,
	icon: PropTypes.node,
	rightSection: PropTypes.node,
	variant: PropTypes.oneOf(['default', 'danger']),
	color: PropTypes.string,
	disabled: PropTypes.bool,
	onClick: PropTypes.func,
	className: PropTypes.string,
	style: PropTypes.object,
};

MenuDivider.propTypes = {
	className: PropTypes.string,
	style: PropTypes.object,
};

MenuLabel.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	style: PropTypes.object,
};

export default FormMenu;
